/**
 * Qué territorio corresponde a UNA entrega.
 *
 * Existe porque un pedido pertenece a **muchos** territorios —en los datos reales
 * hay pedidos con veintiocho— y casi ninguno tiene relación con la ruta que lo
 * reparte. Mostrar los primeros de la lista producía chips que contradecían la
 * entrega: un pedido que se reparte en Comayagua rotulado "TEGUCIGALPA 800".
 *
 * El territorio correcto es la **intersección** entre los territorios del pedido
 * y los que su ruta declara en el maestro. Es un vínculo estructural, no una
 * inferencia sobre cómo están escritos los nombres.
 *
 * Módulo puro: entran dos listas de referencias, sale el modelo de vista.
 */

/**
 * Referencia a un territorio, como la traen el pedido y el maestro de ruta.
 *
 * `id` es `unknown` a propósito: el backend lo manda como número Y como string
 * (ver `territoryIdOf`). Tipearlo `number` era una promesa que el dato no cumple,
 * y hacía que el cruce mal tipado pasara el compilador sin ruido.
 */
export interface RouteRef {
  id?: unknown;
  name?: string;
}

/** Territorio a mostrar en la parada, y si contradice a su ruta. */
export interface DeliveryTerritory {
  /** Nombre(s) a mostrar; `''` cuando no se puede afirmar ninguno. */
  territory: string;
  /**
   * `true` si **ningún** territorio del pedido está entre los que declara su
   * ruta: un pedido asignado a una ruta que no cubre su territorio. Es un dato
   * del maestro a corregir, no un caso a ocultar.
   */
  mismatch: boolean;
}

/**
 * Id de territorio normalizado a **número**.
 *
 * El cruce va por `id` y no por nombre (ver `deliveryTerritory`), pero el id
 * tampoco viene limpio: medido sobre datos reales, el mismo territorio llega
 * como `62` en un pedido y `'62'` en otro, y hay arreglos con los dos tipos
 * mezclados. `new Set([62]).has('62')` es `false`, así que sin normalizar el
 * cruce reporta una discrepancia que no existe — un chip que acusa al dato del
 * pedido por un problema de tipos. Se normaliza acá, que es el módulo más bajo
 * del kit (no importa nada, así que no puede entrar en un ciclo), y lo reusa
 * `planning-catalogs`.
 */
export function territoryIdOf(raw: unknown): number | undefined {
  if (raw === null || raw === undefined || raw === '') return undefined;
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : undefined;
  if (typeof raw === 'string') {
    const parsed = Number(raw.trim());
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

/** Cuántos nombres se listan antes de resumir con "+N". */
const MAX_NAMES = 2;

const NONE: DeliveryTerritory = { territory: '', mismatch: false };

/** Nombres con la convención de la pantalla: hasta dos, y el resto como "+N". */
function label(refs: readonly RouteRef[]): string {
  const names = refs.map((r) => r?.name).filter((n): n is string => !!n);
  if (!names.length) return '';
  return names.length > MAX_NAMES
    ? `${names.slice(0, MAX_NAMES).join(', ')} +${names.length - MAX_NAMES}`
    : names.join(', ');
}

/**
 * Territorio de la entrega.
 *
 * @param orderRoutes territorios del pedido (`order.routes`).
 * @param subRouteRoutes territorios que declara su ruta (`subRoute.routes`).
 *
 * El cruce va por **`id`**, nunca por nombre: los nombres son texto de display y
 * en estos datos ya hay casos con espacios al final, así que un cruce por string
 * reportaría discrepancias falsas sin que nada falle. Se muestra el nombre tal
 * como lo trae el **pedido**, que es el que el usuario ve en la orden.
 *
 * Sin territorios de la ruta no se afirma nada (`territory: ''`, sin
 * discrepancia): el llamador muestra solo la ruta. Marcar discrepancia ahí sería
 * culpar al dato del pedido por un dato que no se pidió.
 */
export function deliveryTerritory(
  orderRoutes: readonly RouteRef[] | undefined,
  subRouteRoutes: readonly RouteRef[] | undefined,
): DeliveryTerritory {
  const mine = (orderRoutes ?? []).filter((r) => !!r);
  if (!mine.length) return NONE;

  const declared = (subRouteRoutes ?? []).filter((r) => !!r);
  if (!declared.length) return NONE;

  const declaredIds = new Set(
    declared.map((r) => territoryIdOf(r.id)).filter((id): id is number => id !== undefined),
  );
  const shared = mine.filter((r) => {
    const id = territoryIdOf(r.id);
    return id !== undefined && declaredIds.has(id);
  });

  if (shared.length) return { territory: label(shared), mismatch: false };

  // El pedido tiene territorios, pero ninguno es de esta ruta.
  return { territory: label(mine), mismatch: true };
}
